// ReconciliationPage.jsx
import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  DatePicker,
  InputNumber,
  Typography,
  Upload,
  message,
  Select,
  Form,
  Row,
  Col,
  Tooltip,
  Grid,
} from "antd";
import { InfoCircleOutlined, UploadOutlined } from "@ant-design/icons";
import "./reconciliationPage.css"
// If your project uses a different API wrapper change this import to your axiosInstance
import { postData, fetchData } from "../../../apiservice/apis";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import {
  currentDateEndTime,
  currentDateTime,
  formatToCompanyTimezone,
  getAccessType,
} from "../../../lib/functions";
import { getAccountBySeachApi } from "../../../apiservice/setting";

const { Title } = Typography;
const { Option } = Select;

const useIsTablet = () => {
  // Simple replacement for material-ui useMediaQuery("(min-width:700px) and (max-width:950px)")
  const [isTablet, setIsTablet] = useState(
    typeof window !== "undefined" && window.innerWidth >= 700 && window.innerWidth <= 950
  );
  useEffect(() => {
    const handler = () => {
      setIsTablet(window.innerWidth >= 700 && window.innerWidth <= 950);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isTablet;
};

const ReconciliationPage = ({ authInfo }) => {
  const [accountId, setAccountId] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [closingAmount, setClosingAmount] = useState(0);
  const [reconciliationList, setReconciliationList] = useState([]);
  const [toAccs, setToAccs] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [difference, setDifference] = useState(0);
  const [showDifference, setShowDifference] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [clearedBalance, setClearedBalance] = useState(0);
  const [newClearedBalance, setNewClearedBalance] = useState(0);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [lastReconciledDate, setLastReconciledDate] = useState(currentDateTime);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(dayjs());
  const [isClosingBalanceLocked, setIsClosingBalanceLocked] = useState(false);
    let [access_type, access_type_set] = useState("");

  useEffect(() => {
    const access = getAccessType("bank_transactions");
    access_type_set(access);
  }, []);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const branchId = authInfo?.userInfo?.branch_id;
  const userId = authInfo?.userInfo?.user_id;
  const currency = authInfo?.currency;

  const isTablet = useIsTablet();
  const hasSelection = selectedRowKeys.length > 0;

  // 🔹 Fetch accounts

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const res = await getAccountBySeachApi({
          multiType: ["cash_in_hand", "bank_account"],
        });
        setToAccs(res.data || []);
      } catch (err) {
        Swal.fire({
          title: "Failed to load accounts",
          text: err.response?.data?.message || "Please try again later",
          icon: "error",
        });
      }
    };
    fetchAccounts();
  }, []);

  // Fetch last reconciled status
  const fetchLastReconciledStatus = async () => {
    if (!accountId) return;
    try {
      const res = await fetchData(
        `/accounts/last-reconciled-status?branch_id=${branchId}&created_by=${userId}&account_id=${accountId}`
      );
      const status = res.data.data;

      setFromDate(status?.last_reconciled_date ? dayjs(status.last_reconciled_date) : null);
      setLastReconciledDate(status?.last_reconciled_date || "1970-01-01");
      setClearedBalance(Number(status?.cleared_balance || 0));
      setNewClearedBalance(Number(status?.cleared_balance || 0));
      setOpeningBalance(Number(status?.cleared_balance || 0));
    } catch (err) {
      console.error("Failed to fetch last reconciled status", err);
    }
  };

  useEffect(() => {
    if (accountId) {
      fetchLastReconciledStatus();
    }
  }, [accountId]);

  // amount sign helper
  const rowAmount = (row) => (row.debit ? parseFloat(row.debit) : -parseFloat(row.credit || 0));

  // Fetch reconciliation list (keeps old UI behavior -> uses currentDateEndTime)
  const fetchList = async (latestClearedBalance = clearedBalance) => {
    if (!accountId) {
      return Swal.fire({ title: "Please select the account.", icon: "warning" });
    }
    if (!toDate) {
      return Swal.fire({ title: "Please select the end date.", icon: "warning" });
    }
    if (!closingAmount) {
      return Swal.fire({
        title: "Please type the closing amount from the bank statement.",
        icon: "warning",
      });
    }

    const toDateEndTimeUTC = currentDateEndTime(toDate);
    const toDateEnd = dayjs(toDate).endOf("day").format("YYYY-MM-DD HH:mm:ss");

    setIsLoading(true);
    try {
      const res = await fetchData(
        `/accounts/reconciliation-list?branch_id=${branchId}&created_by=${userId}&account_id=${accountId}&to_date=${toDateEnd}`
      );

      const list = res.data.data.map((row, index) => ({
        ...row,
        key: index,
        date: row.date ? formatToCompanyTimezone(row.date) : "",
      }));

      const preselected = list.filter((row) => row.reconciled).map((row) => row.key);

      setReconciliationList(list);
      setSelectedRowKeys(preselected);

      const totalPreselected = list
        .filter((row) => preselected.includes(row.key))
        .reduce((sum, row) => sum + rowAmount(row), 0);

      const safeClosing = Number(closingAmount) || 0;
      const safeCleared = Number(latestClearedBalance) || 0;

      const effectiveCleared = safeCleared + totalPreselected;
      setNewClearedBalance(effectiveCleared);
      setDifference((safeClosing - effectiveCleared).toFixed(2));
      setIsClosingBalanceLocked(true);
      setShowDifference(true);
    } catch (err) {
      console.error(err);
      Swal.fire({ title: "Failed to fetch reconciliation list.", icon: "error" });
    }
    setIsLoading(false);
  };

  // recalc when selection/closing changes (mirrors old logic)
  useEffect(() => {
    if (reconciliationList.length === 0) return;

    let delta = 0;
    reconciliationList.forEach((row) => {
      const isSelected = selectedRowKeys.includes(row.key);
      const wasSelectedBefore = !!row.reconciled;
      const amount = row.debit ? parseFloat(row.debit) : -parseFloat(row.credit || 0);

      if (!wasSelectedBefore && isSelected) delta += amount;
    });

    const safeClosing = Number(closingAmount) || 0;
    const safeCleared = Number(clearedBalance) || 0;

    const recalculatedCleared = safeCleared + delta;
    setNewClearedBalance(recalculatedCleared);
    setDifference((safeClosing - recalculatedCleared).toFixed(2));
  }, [closingAmount, selectedRowKeys, reconciliationList, newClearedBalance]);

  const handleSelectionChange = (keys) => {
    setSelectedRowKeys(keys);

    let delta = 0;
    reconciliationList.forEach((row) => {
      const isSelectedNow = keys.includes(row.key);
      const wasSelectedBefore = !!row.reconciled;
      const amount = row.debit ? parseFloat(row.debit) : -parseFloat(row.credit || 0);

      if (!wasSelectedBefore && isSelectedNow) delta += amount;
    });

    const safeClosing = Number(closingAmount) || 0;
    const safeCleared = Number(newClearedBalance) || 0;

    const recalculatedCleared = safeCleared + delta;
    setNewClearedBalance(recalculatedCleared);
    setDifference((safeClosing - recalculatedCleared).toFixed(2));
  };

  const handleReconcileClick = async () => {
    if (Number(difference) !== 0) {
      return Swal.fire({
        title: "Difference must be zero before reconciling!",
        icon: "warning",
      });
    }
    if (!selectedRowKeys.length) {
      return Swal.fire({
        title: "Please select rows to reconcile.",
        icon: "info",
      });
    }

    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to mark the selected rows as reconciled?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "OK",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      const selectedRows = reconciliationList
        .filter((row) => selectedRowKeys.includes(row.key))
        .map(({ reconciled, key, ...rest }) => rest);

      const payload = {
        selected_transactions: selectedRows,
        account_id: accountId,
        branch_id: branchId,
        created_by: userId,
        // created_date: toDate.format("YYYY-MM-DD"),
        created_date: currentDateEndTime(toDate),
        cleared_balance: newClearedBalance,
      };

      try {
        await postData("/accounts/reconciliation-mark", payload);
        Swal.fire({ title: "Reconciliation Successful!", icon: "success" });

        // fetch latest cleared balance
        const res = await fetchData(
          `/accounts/last-reconciled-status?branch_id=${branchId}&created_by=${userId}&account_id=${accountId}`
        );
        const status = res.data.data;
        const latestCleared = Number(status?.cleared_balance || 0);
        setFromDate(status?.last_reconciled_date ? dayjs(status.last_reconciled_date) : null);

        setClearedBalance(latestCleared);
        setNewClearedBalance(latestCleared);

        setIsClosingBalanceLocked(false);
        // reset
        setClosingAmount(0);
        setCsvFile(null);
        setReconciliationList([]);
        setSelectedRowKeys([]);
        setDifference(0);
        setShowDifference(false);
      } catch (err) {
        console.error(err);
        Swal.fire({
          title: "Reconciliation Failed",
          text: err.response?.data?.message || "Something went wrong",
          icon: "error",
        });
      }
    });
  };

  const columns = [
    { title: "No.", dataIndex: "serial", render: (_, __, index) => index + 1 },
    { title: "Date", dataIndex: "date", render: (v) =>
    v ? formatToCompanyTimezone(v).split(" ")[0] : "-", },
    { title: "Source", dataIndex: "source" },
    { title: "Ref No.", dataIndex: "ref_no" },
    { title: "Description", dataIndex: "description" },
    { title: "Debit", dataIndex: "debit" },
    { title: "Credit", dataIndex: "credit" },
  ];

  return (
    <div style={{ padding: screens.xs ? "0rem": "2rem" }}>
      <Title level={3}>Reconciliation</Title>

      <Form layout="vertical">
        <Row gutter={[16, 16]} className="reconciliation-head">
          {/* Account Selector */}
          <Col xs={24} sm={12} xl={6}>
            <Form.Item label="Select Account" required>
              <Select
                placeholder="Select Account"
                style={{ width: "100%" }}
                value={accountId}
                onChange={setAccountId}
              >
                {toAccs?.map((acc) => (
                  <Option key={acc?.acc_id} value={acc?.acc_id}>
                    {acc?.acc_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* Dates */}
          <Col xs={24} sm={12} xl={3}>
            <Form.Item label="From Date">
              <DatePicker value={fromDate} disabled format="DD-MM-YYYY" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={3}>
            <Form.Item label="To Date">
              <DatePicker
                value={toDate}
                onChange={setToDate}
                disabledDate={(current) => {
                  if (!fromDate) return false;
                  return current && current < fromDate.startOf("day");
                }}
                format="DD-MM-YYYY"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          {/* Closing Balance */}
          <Col xs={24} sm={12} xl={4}>
            <Form.Item
              label={
                <span>
                  Closing Balance{" "}
                  <span className="hide-mobile">(Bank)</span>
                </span>
              }
              required
            >

              <Tooltip title="Enter closing balance from the bank statement">
                <InputNumber
                  placeholder="Enter amount"
                  value={closingAmount}
                  onClick={() => {
                    if (
                      !isClosingBalanceLocked &&
                      closingAmount !== null
                    ) {
                      setClosingAmount(null);
                    }
                  }}
                  onChange={setClosingAmount}
                  style={{ width: "100%" }}
                  onWheel={(e) => e.target.blur()}
                  disabled={isClosingBalanceLocked}
                />
              </Tooltip>
            </Form.Item>

            {isClosingBalanceLocked && (
              <Button
                type="link"
                onClick={() => {
                  setIsClosingBalanceLocked(false);
                  setClosingAmount(0);
                  setCsvFile(null);
                  setReconciliationList([]);
                  setSelectedRowKeys([]);
                  setDifference(0);
                  setNewClearedBalance(clearedBalance);
                  setShowDifference(false);
                }}
              >
                Reset Balance
              </Button>
            )}
          </Col>

          {/* Buttons */}
          <Col
            xs={24}
            sm={12}
            xl={4}
            className="recon-btn-group"
          >
            <Button type="primary" onClick={() => fetchList()}>
              Get List
            </Button>

            {access_type == "WRITE" && <Upload
              accept=".csv"
              beforeUpload={(file) => {
                const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");
                if (!isCSV) {
                  message.error("Only CSV files are allowed!");
                  return Upload.LIST_IGNORE;
                }
                setCsvFile(file);
                message.success(`${file.name} selected successfully`);
                return false;
              }}
              showUploadList={false}              
            >
              <Button icon={<UploadOutlined />} >Upload CSV</Button>
            </Upload>}

            <Button
              type="dashed"
              onClick={() => {}}
              disabled={!csvFile || reconciliationList.length === 0}
            >
              Auto Match
            </Button>
          </Col>
        </Row>
      </Form>

      {/* Balances */}
      <div style={{ marginBottom: "1rem", fontWeight: "bold", fontSize: "16px", textAlign: screens.xs ? "center" : "left" }}>
        Statement Closing Balance:{" "}
       <br style={{ display: screens.xs ? "block" : "none" }} />
        <span style={{ display: screens.xs && "block", textAlign: screens.xs ? "center" : "left" }} >{closingAmount ? closingAmount.toFixed(2) : "0.00"} {currency}</span>
      </div>

      <div
        style={{
          marginBottom: "1rem",
          fontWeight: "bold",
          fontSize: "16px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: screens.xs ? "wrap" : "nowrap",
          justifyContent: screens.xs ? "center" : "flex-start",
        }}
      >
        <span style={{ display: "flex", alignItems: "center" }}>
          Cleared Balance:
          <Tooltip title="Left = Last reconciled balance. Right = Updated balance after selecting transactions.">
            <InfoCircleOutlined
              style={{
                fontSize: "14px",
                color: "#888",
                paddingLeft: "6px",
                cursor: "pointer",
              }}
            />

          </Tooltip>
        </span>
        <br style={{ display: screens.xs ? "block" : "none" }} />
        <span style={{ color: "#444", display: screens.xs ? "100%" : "auto", textAlign: screens.xs ? "center" : "left" }}>{clearedBalance.toFixed(2)} {currency}</span>

        {hasSelection && newClearedBalance !== clearedBalance && (
          <>
            <span
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "#888",
                margin: "0 6px",
              }}
            >
              →
            </span>
            <span style={{ color: "blue", fontWeight: "bold" }}>
              {newClearedBalance.toFixed(2)} {currency}
            </span>
          </>
        )}
      </div>

      {showDifference && hasSelection && (
        <div
          style={{
            marginBottom: "1rem",
            fontWeight: "bold",
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span style={{ display: "flex", alignItems: "center" }}>
            Difference:
            <Tooltip title="Difference = Statement Closing Balance – Cleared Balance">
              <InfoCircleOutlined
                style={{
                  fontSize: "14px",
                  color: "#888",
                  paddingLeft: "6px",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          </span>
          <span style={{ color: Number(difference) === 0 ? "green" : "red" }}>
            {difference} {currency}
          </span>
          {Number(difference) === 0 ? (
            <span style={{ color: "green", fontSize: "18px" }}>✅</span>
          ) : (
            <span style={{ color: "red", fontSize: "18px" }}>❌</span>
          )}
        </div>
      )}

      {/* Table */}
      <Table
        loading={isLoading}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys,
          onChange: handleSelectionChange,
          getCheckboxProps: (record) => ({
            disabled: record.reconciled === 1,
          }),
        }}
        columns={columns}
        scroll={{x:"max-content"}}
        dataSource={reconciliationList}
        pagination={false}
        bordered
        rowClassName={(record) => (record.reconciled ? "reconciled-row" : "")}
      />

      {/* Reconcile Button */}
      <Button
        type="primary"
        disabled={Number(difference) !== 0 || selectedRowKeys.length === 0}
        onClick={handleReconcileClick}
        loading={isLoading}
        style={{
          marginTop: "1rem",
          backgroundColor: Number(difference) === 0 && selectedRowKeys.length > 0 ? "green" : undefined,
          borderColor: Number(difference) === 0 && selectedRowKeys.length > 0 ? "green" : undefined,
          width: screens.xs && "100%"
        }}
      >
        Reconcile
      </Button>

      <div style={{ marginTop: "0.5rem", fontSize: "14px", color: "#888" }}>
        * Reconcile button is enabled only when Difference is zero and at least one row is selected.
      </div>
    </div>
  );
};

export default ReconciliationPage;
